// Utility functions
import { locale as i18nLocale } from 'virtual:i18n';
import { IMAGE_COMPRESSION_CONFIG } from './config.mjs';

// Log message to UI
export function logMessage(message, type = 'info') {

    console.log(message);
}

// Send analytics event via gtag
export function trackEvent(eventName, eventParams = {}) {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, eventParams);
    } else {
        console.warn('Analytics mapping missing:', eventName, eventParams);
    }
}

// Generate a random client ID
export function generateClientId() {
    return 'client-' + Math.random().toString(36).substring(2, 15);
}

// Generate a random subscription ID
export function generateSubscriptionId() {
    return 'sub-' + Math.random().toString(36).substring(2, 15);
}

// Generate or retrieve a temporary webapp ID for anonymous usage
export function getWebId() {
    let webId = localStorage.getItem('splitpal:webId');
    if (!webId) {
        webId = 'web-' + Math.random().toString(36).substring(2, 15) + '-' + Date.now().toString(36);
        localStorage.setItem('splitpal:webId', webId);
    }
    return webId;
}

// Clear messages function
export function clearMessages() {
    const messagesEl = document.getElementById('messages');
    messagesEl.innerHTML = '';
    logMessage('Messages cleared', 'info');
}

// Parse URL path to extract groupId
export function parseUrlForGroupId() {
    // Get the pathname (without hash)
    const path = window.location.pathname;

    // Check if path matches the pattern /home/:groupId/receipt/:expenseId
    const receiptPattern = /^\/home\/([^\/]+)\/receipt\/([^\/]+)$/;
    const receiptMatch = path.match(receiptPattern);
    if (receiptMatch) {
        return receiptMatch[1]; // Return the groupId
    }

    // Check if path matches the pattern /home/:groupId
    const homePattern = /^\/home\/([^\/]+)$/;
    const homeMatch = path.match(homePattern);
    if (homeMatch) {
        return homeMatch[1]; // Return the groupId
    }

    // If no match found, return null
    return null;
}

// Parse URL path to determine if we're in receipt view and extract expenseId
export function parseUrlForReceiptView() {
    // Get the pathname (without hash)
    const path = window.location.pathname;

    // Check if path matches the pattern /home/:groupId/receipt/:expenseId
    const receiptPattern = /^\/home\/([^\/]+)\/receipt\/([^\/]+)$/;
    const receiptMatch = path.match(receiptPattern);

    if (receiptMatch) {
        return {
            isReceiptView: true,
            groupId: receiptMatch[1],
            expenseId: receiptMatch[2]
        };
    }

    // Not in receipt view
    return {
        isReceiptView: false,
        groupId: null,
        expenseId: null
    };
}
// Show app spinner
export function showSpinner(text) {
    const spinner = document.getElementById('appSpinner');
    const spinnerText = document.getElementById('appSpinnerText');
    if (spinner) {
        if (spinnerText && text !== undefined) {
            spinnerText.textContent = text;
            spinnerText.style.display = text ? 'block' : 'none';
        }
        spinner.style.display = 'flex';
    }
}

// Update spinner text
export function updateSpinnerText(text) {
    const spinnerText = document.getElementById('appSpinnerText');
    if (spinnerText) {
        spinnerText.textContent = text;
        spinnerText.style.display = text ? 'block' : 'none';
    }
}

// Hide app spinner
export function hideSpinner() {
    const spinner = document.getElementById('appSpinner');
    if (spinner) {
        spinner.style.display = 'none';

        // Reset text so it doesn't linger for the next generic spinner
        const spinnerText = document.getElementById('appSpinnerText');
        if (spinnerText) {
            spinnerText.textContent = '';
            spinnerText.style.display = 'none';
        }
    }
}

// Currency formatting overrides for specific currencies
export const CURRENCY_OVERRIDES = {
    'IDR': { fractionSize: 0, symbol: 'Rp' }
};

// Format currency using Intl.NumberFormat
export function formatCurrency(amount, currency = 'USD') {
    if (amount === undefined || amount === null) return '';

    try {
        const uppercaseCurrency = currency.toUpperCase();
        const override = CURRENCY_OVERRIDES[uppercaseCurrency];
        const fractionDigits = override !== undefined ? override.fractionSize : undefined;

        const formatter = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'symbol', // Use symbol like $
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        });
        
        let formatted = formatter.format(amount);
        
        if (override && override.symbol) {
            formatted = formatted.replace(uppercaseCurrency, override.symbol);
        }
        
        return formatted;
    } catch (e) {
        // Fallback for invalid currency codes
        console.error('Currency formatting error:', e);
        return `${amount} ${currency}`;
    }
}

// Format date using Intl.DateTimeFormat — locale-aware
export function formatDate(dateString) {
    if (!dateString) return '';

    try {
        // Date-only strings like "2026-03-12" are parsed by `new Date()` as
        // UTC midnight per the ECMAScript spec. In timezones behind UTC (e.g.
        // US Eastern = UTC-4) this silently rolls back to the previous day.
        // Replacing dashes with slashes forces local-time parsing, matching
        // how Flutter / the mobile app displays the same stored date.
        const normalized = dateString.length === 10
            ? dateString.replace(/-/g, '/')   // "2026-03-12" → "2026/03/12" (local time)
            : dateString;                      // full ISO timestamp — leave as-is
        const date = new Date(normalized);
        const formatter = new Intl.DateTimeFormat(i18nLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        return formatter.format(date);
    } catch (e) {
        console.error('Date formatting error:', e);
        return dateString;
    }
}


export function getLinkedParticipants(group, index) {
    if (group.settlement && group.settlement.linkedParticipantGroups && group.settlement.linkedParticipantGroups[index]) {
        return group.settlement.linkedParticipantGroups[index].participants;
    }
    return [index];
}

export function getParticipantName(group, index) {
    const indices = getLinkedParticipants(group, index);
    return indices.map(i => group.participants[i]).join(' & ');
}

/**
 * Compresses an image file client-side using Canvas API if it exceeds a threshold (e.g., 1MB).
 * Returns the compressed file or the original file as a fallback.
 */
export async function compressImageIfNeeded(file, options = {}) {
    if (!file || !file.type.startsWith('image/')) {
        return file;
    }

    const config = { ...IMAGE_COMPRESSION_CONFIG, ...options };
    const maxSizeBytes = config.maxSizeMB * 1024 * 1024;
    if (file.size <= maxSizeBytes) {
        console.log(`Image is small (${(file.size / 1024).toFixed(1)} KB). Skipping compression.`);
        return file;
    }

    console.log(`Compressing receipt image from ${(file.size / 1024 / 1024).toFixed(2)} MB...`);
    try {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let width = img.width;
                    let height = img.height;

                    // Scale down to fit within max dimensions while maintaining aspect ratio
                    let scale = 1;
                    if (width > config.maxWidth || height > config.maxHeight) {
                        scale = Math.min(config.maxWidth / width, config.maxHeight / height);
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width * scale;
                    canvas.height = height * scale;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                // Construct JPEG file with .jpg extension
                                const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                });
                                console.log(`Compressed size: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
                                resolve(compressedFile);
                            } else {
                                reject(new Error('Canvas toBlob failed'));
                            }
                        },
                        'image/jpeg',
                        config.quality
                    );
                };
                img.onerror = () => reject(new Error('Image failed to load'));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error('FileReader failed'));
            reader.readAsDataURL(file);
        });
    } catch (err) {
        console.warn('Image compression failed; falling back to original file:', err);
        return file;
    }
}
